import { HKPImage } from "../../media/HKPImage";
import {
  COLLECTOR_PHOTO_EXPIRED_MESSAGE,
  collectorPhotoProblem,
  isPhotoExpiredError,
} from "../../media/collectorMediaUpload";
import React, { useEffect, useRef, useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { Spinner, Badge, Button } from "reactstrap";
import MyCollectionLayout from "./MyCollectionLayout";
import McBackLink from "./McBackLink";
import CollectorPieceFields, { collectorJournalBody, collectorPieceFromItem } from "./CollectorPieceFields";
import CollectorImageZoom from "./CollectorImageZoom";
import CollectorItemQrPanel from "./CollectorItemQrPanel";
import QrPrintModal from "./QrPrintModal";
import { useCollectorMasters } from "./useCollectorMasters";
import { ownerPieceTitle } from "./collectorPieceForm";
import OwnershipClaimCard from "./OwnershipClaimCard";
import McAddLaterCard from "./McAddLaterCard";
import { getItemCertificate, getCollectorItem, isCollectorSessionError, updateCollectorItem, archiveCollectorItem, requestItemAccess, getItemAccessRequest, collectorDetailsAllowed } from "../../services/collectorApi";
import { collectorLoginPath, getCollectorToken } from "../../helpers/collectorSession";
import { collectorMongoId, collectorRecordKey, publicPieceUrl } from "../../helpers/collectorIds";
import { PUBLIC_DISPLAY_FIELD_OPTIONS, parsePurchasePrice, uploadCollectorPhoto, assertCollectorImage } from "../../helpers/collectorUpload";
import { LuxuryCardFieldSource } from "../../helpers/collectorQrDownload";
import { itemRoom, sawThisLabel } from "../../helpers/collectorRooms";

const DETAIL_ROWS: { key: string; label: string }[] = [
  { key: "itemCategory", label: "Category" },
  { key: "color", label: "Color" },
  { key: "craftType", label: "Craft type" },
  { key: "fabric", label: "Fabric" },
  { key: "clusterType", label: "Region / cluster" },
  { key: "occasion", label: "Occasion" },
  { key: "acquiredDate", label: "When you got it" },
  { key: "acquiredPlace", label: "Where you got it" },
  { key: "makerName", label: "Who made it" },
];

const pretty = (value?: string) => (value ? value.replace(/_/g, " ") : "");

function cardFieldSource(
  item: {
    publicDisplayFields?: string[];
    itemCategory?: string;
    color?: string;
    craftType?: string;
    fabric?: string;
    clusterType?: string;
    occasion?: string;
    acquiredPlace?: string;
    makerName?: string;
  },
  preview?: { category?: string; color?: string; craftType?: string; cluster?: string },
  hkpLocked?: boolean
): LuxuryCardFieldSource {
  const cert = !hkpLocked && preview ? preview : undefined;
  const allow = item.publicDisplayFields || [];
  const source: LuxuryCardFieldSource = {
    publicDisplayFields: allow,
    itemCategory: item.itemCategory || cert?.category,
    color: item.color || cert?.color,
    craftType: item.craftType || cert?.craftType,
    fabric: item.fabric,
    clusterType: item.clusterType || cert?.cluster,
    occasion: item.occasion,
    acquiredPlace: item.acquiredPlace,
    makerName: item.makerName,
  };
  if (cert && allow.length === 0) {
    source.publicDisplayFields = [
      source.itemCategory ? "itemCategory" : "",
      source.color ? "color" : "",
      source.craftType ? "craftType" : "",
      source.clusterType ? "clusterType" : "",
    ].filter(Boolean);
  }
  return source;
}

function itemGallery(item: { title?: string; coverPhotoUrl?: string; additionalImageUrls?: string[] }): { src: string; alt: string }[] {
  const gallery: { src: string; alt: string }[] = [];
  const seen = new Set<string>();
  const pushPhoto = (src?: string, alt?: string) => {
    const url = String(src || "").trim();
    if (!url || seen.has(url)) return;
    seen.add(url);
    gallery.push({ src: url, alt: alt || item.title || "Photo" });
  };
  pushPhoto(item.coverPhotoUrl, item.title || "Cover");
  (item.additionalImageUrls || []).forEach((url, index) => {
    pushPhoto(url, `${item.title || "Photo"} ${index + 2}`);
  });
  return gallery;
}

type CertificateSummary = {
  isSample?: boolean;
  statusBadge?: string;
  certNumber?: string;
  productTitle?: string;
  productPhotoUrl?: string;
  artisanName?: string;
  cluster?: string;
  craftType?: string;
  studioName?: string;
  verifyUrl?: string;
};

const CertificateTab: React.FC<{ itemId: string; isSample?: boolean }> = ({ itemId, isSample }) => {
  const [cert, setCert] = useState<CertificateSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getItemCertificate(itemId)
      .then((res) => setCert(res.data?.data || null))
      .finally(() => setLoading(false));
  }, [itemId]);

  if (loading) return <Spinner size="sm" />;
  if (!cert) return <p className="text-muted">Certificate unavailable</p>;

  return (
    <div className="border rounded p-3">
      {isSample || cert.isSample === true ? (
        <Badge color="warning" className="mb-2">SAMPLE — demo certificate</Badge>
      ) : null}
      {cert.statusBadge && (
        <Badge color="secondary" className="mb-2 ms-1">{cert.statusBadge}</Badge>
      )}
      {cert.productPhotoUrl && (
        <HKPImage allowMaster={false} preset="PRODUCT_DETAIL" legacyUrl={cert.productPhotoUrl} alt="" className="img-fluid rounded mb-3" style={{ maxHeight: 200 }} />
      )}
      <dl className="row mb-0 small">
        {cert.certNumber && <><dt className="col-sm-4">Cert #</dt><dd className="col-sm-8">{cert.certNumber}</dd></>}
        {cert.artisanName && <><dt className="col-sm-4">Artisan</dt><dd className="col-sm-8">{cert.artisanName}</dd></>}
        {cert.cluster && <><dt className="col-sm-4">Cluster</dt><dd className="col-sm-8">{cert.cluster}</dd></>}
        {cert.craftType && <><dt className="col-sm-4">Craft</dt><dd className="col-sm-8">{cert.craftType}</dd></>}
        {cert.studioName && <><dt className="col-sm-4">Studio</dt><dd className="col-sm-8">{cert.studioName}</dd></>}
      </dl>
      {cert.verifyUrl && (
        <a
          href={cert.verifyUrl}
          target="_blank"
          rel="noreferrer"
          className="mc-btn-secondary mt-2"
        >
          View full certificate
        </a>
      )}
    </div>
  );
};

const MyCollectionItemDetail: React.FC = () => {
  const { itemId: paramItemId } = useParams<{ itemId: string }>();
  const location = useLocation();
  const itemId = paramItemId || location.pathname.split("/").filter(Boolean)[2];
  const history = useHistory();
  const [item, setItem] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [qrCardOnly, setQrCardOnly] = useState(false);
  const [qrFormat, setQrFormat] = useState<"minimal" | "luxury">("luxury");
  const [zoomIndex, setZoomIndex] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [archiving, setArchiving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [coverProgress, setCoverProgress] = useState<number | null>(null);
  const [coverPreview, setCoverPreview] = useState("");
  const coverPreviewRef = useRef("");
  coverPreviewRef.current = coverPreview;
  const dropCoverPreview = () => {
    if (coverPreviewRef.current) URL.revokeObjectURL(coverPreviewRef.current);
    coverPreviewRef.current = "";
    setCoverPreview("");
  };
  useEffect(() => () => { if (coverPreviewRef.current) URL.revokeObjectURL(coverPreviewRef.current); }, []);
  const [journal, setJournal] = useState(collectorPieceFromItem({}));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [accessRequest, setAccessRequest] = useState<{ status?: string } | null>(null);
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [mediaBusy, setMediaBusy] = useState(false);
  const { masters } = useCollectorMasters();

  useEffect(() => {
    if (!getCollectorToken()) {
      history.push(collectorLoginPath(location.pathname + location.search));
      return;
    }
    if (!itemId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    getCollectorItem(itemId)
      .then((itemRes) => {
        if (cancelled) return;
        const found = itemRes.data?.data;
        if (!found || itemRes.data?.success === false) {
          setItem(null);
          setNotFound(true);
          return;
        }
        setItem(found);
        setJournal(collectorPieceFromItem(found));
        // Arriving from quick add's "Saved" screen: open the editor or the print card straight away.
        const intent = new URLSearchParams(location.search);
        setEditing(intent.get("edit") === "1" && found.itemType === "EXTERNAL");
        if (intent.get("print") === "1") {
          setQrCardOnly(false);
          setShowQr(true);
        }
        if (intent.has("edit") || intent.has("print")) history.replace(location.pathname);
        const room = itemRoom(found);
        const scannedLike = room === "scanned" || (room === "demo" && found.itemType === "HKP");
        if (scannedLike && found.accessState === "ACCESS_LOCKED") {
          getItemAccessRequest(collectorRecordKey(found) || itemId)
            .then((reqRes) => setAccessRequest(reqRes.data?.data || { status: "NONE" }))
            .catch(() => setAccessRequest({ status: "NONE" }));
        } else {
          setAccessRequest(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (isCollectorSessionError(err)) {
          // Opened from a bookmark or QR after the session ran out: sign in and come back here.
          history.push(collectorLoginPath(location.pathname + location.search));
          return;
        }
        setItem(null);
        setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [itemId, history]);

  const saveJournal = async () => {
    if (!itemId) return;
    // A photo-only piece may keep no name; the server keeps its "Untitled piece" title.
    if (!journal.title.trim() && ownerPieceTitle(item?.title)) {
      setFieldErrors({ title: "Title is required." });
      setError("Title is required.");
      return;
    }
    try {
      parsePurchasePrice(journal.purchasePrice);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Enter a valid price.";
      setFieldErrors({ purchasePrice: message });
      setError(message);
      return;
    }
    setFieldErrors({});
    setSaving(true);
    setError("");
    try {
      const res = await updateCollectorItem(collectorRecordKey(item) || itemId, collectorJournalBody(journal));
      const updated = res.data?.data;
      if (updated) {
        setItem(updated);
        setJournal(collectorPieceFromItem(updated));
      }
      dropCoverPreview();
      setEditing(false);
    } catch (e: any) {
      const code = e?.response?.data?.message || e?.response?.data?.error;
      setError(
        code === "PASSKEY_REQUIRED_FOR_PRIVATE"
          ? "Set an account passkey in Settings before making a piece private."
          : isPhotoExpiredError(e)
            ? COLLECTOR_PHOTO_EXPIRED_MESSAGE
            : code || "Could not save your journal"
      );
      setJournal((prev) => ({ ...prev, visibility: item?.visibility || "PUBLIC" }));
    } finally {
      setSaving(false);
    }
  };

  const handleCoverFile = async (file: File) => {
    try {
      assertCollectorImage(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Please choose a photo.");
      return;
    }
    const localPreview = URL.createObjectURL(file);
    dropCoverPreview();
    setCoverPreview(localPreview);
    setUploading(true);
    setCoverProgress(0);
    setError("");
    try {
      const url = await uploadCollectorPhoto(file, setCoverProgress);
      // The phone's own copy stays on screen until the piece is saved (a new "hkp-media:" photo has no link yet).
      setJournal((prev) => ({ ...prev, coverPhotoUrl: url }));
    } catch (err) {
      dropCoverPreview();
      const problem = collectorPhotoProblem(err);
      if (problem.kind === "session") {
        history.push(collectorLoginPath(location.pathname + location.search));
        return;
      }
      setError(problem.kind === "other" ? "Cover photo upload failed. Try again." : problem.message);
    } finally {
      setUploading(false);
      setCoverProgress(null);
    }
  };

  const archivePiece = async () => {
    const id = collectorRecordKey(item) || itemId;
    if (!id) return;
    const room = itemRoom(item);
    const back = room === "scanned" ? "/my-collection/scanned" : "/my-collection";
    if (!window.confirm("Archive this piece? It will leave this list. You can restore it from Settings.")) {
      return;
    }
    setArchiving(true);
    setError("");
    try {
      await archiveCollectorItem(id);
      history.push(back);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Could not archive this piece");
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <MyCollectionLayout>
        <div className="text-center py-5"><Spinner /></div>
      </MyCollectionLayout>
    );
  }

  if (notFound || !item) {
    return (
      <MyCollectionLayout>
        <McBackLink to="/my-collection">Back to My pieces</McBackLink>
        <p className="text-muted">This piece could not be found.</p>
      </MyCollectionLayout>
    );
  }

  const room = itemRoom(item);
  const isDemo = room === "demo";
  const isHkp = item.itemType === "HKP";
  const isExternal = item.itemType === "EXTERNAL";
  const scannedLike = room === "scanned" || (isDemo && isHkp);
  const piecesLike = room === "pieces" || (isDemo && isExternal);
  const backTo = scannedLike ? "/my-collection/scanned" : "/my-collection";
  const backLabel = scannedLike ? "Back to Scanned" : "Back to My pieces";
  const mongoId = collectorMongoId(item) || itemId;
  const detailsOpen = collectorDetailsAllowed(item.accessState);
  const hkpLocked = scannedLike && item.accessState === "ACCESS_LOCKED";
  const preview = detailsOpen ? item.productPreview : undefined;
  const gallery = itemGallery({
    ...item,
    coverPhotoUrl: item.coverPhotoUrl || preview?.coverPhotoUrl,
  });
  const showSharePrint = piecesLike && !isDemo;
  const showCertificate = (scannedLike && isHkp && detailsOpen) || (piecesLike && isHkp && item.status === "OWNED" && detailsOpen);

  const submitAccessRequest = async () => {
    const id = collectorRecordKey(item) || itemId;
    if (!id || requestingAccess) return;
    setRequestingAccess(true);
    setError("");
    try {
      const res = await requestItemAccess(id);
      setAccessRequest(res.data?.data || { status: "PENDING" });
    } catch (e: any) {
      const msg = e?.response?.data?.message || "Could not send the request";
      setError(msg === "ACCESS_REQUEST_PENDING" ? "A request is already waiting with the workshop." : msg);
    } finally {
      setRequestingAccess(false);
    }
  };

  const photos = gallery.length > 0 && (
    <div className="mc-item-photos mb-4">
      <button
        type="button"
        className="mc-item-cover"
        onClick={() => setZoomIndex(0)}
        aria-label={`Zoom ${gallery[0].alt}`}
      >
        <HKPImage allowMaster={false} preset="PRODUCT_DETAIL" legacyUrl={gallery[0].src} alt={gallery[0].alt} />
        <span className="mc-zoom-hint">Click to zoom</span>
      </button>
      {gallery.length > 1 && (
        <div className="mc-thumb-grid mt-2">
          {gallery.slice(1).map((photo, index) => (
            <button
              type="button"
              key={`${photo.src}-${index}`}
              className="mc-thumb mc-thumb-zoom"
              onClick={() => setZoomIndex(index + 1)}
              aria-label={`Zoom ${photo.alt}`}
            >
              <HKPImage allowMaster={false} preset="PRODUCT_DETAIL" legacyUrl={photo.src} alt="" />
            </button>
          ))}
        </div>
      )}
      <CollectorImageZoom
        images={gallery}
        index={zoomIndex}
        onClose={() => setZoomIndex(null)}
        onIndexChange={setZoomIndex}
      />
    </div>
  );

  const privateFacts = (
    <dl className="row mt-3 mb-0">
      {DETAIL_ROWS.filter((row) => item[row.key]).map((row) => (
        <React.Fragment key={row.key}>
          <dt className="col-sm-4 text-muted">{row.label}</dt>
          <dd className="col-sm-8">{pretty(item[row.key])}</dd>
        </React.Fragment>
      ))}
      {item.purchasePrice != null && item.purchasePrice !== "" && (
        <>
          <dt className="col-sm-4 text-muted">Price paid</dt>
          <dd className="col-sm-8">{item.purchaseCurrency || "INR"} {item.purchasePrice}</dd>
        </>
      )}
      {item.purchaseGeo?.lat != null && item.purchaseGeo?.lng != null && (
        <>
          <dt className="col-sm-4 text-muted">Bought at (GPS)</dt>
          <dd className="col-sm-8">
            <a
              href={`https://www.openstreetmap.org/?mlat=${item.purchaseGeo.lat}&mlon=${item.purchaseGeo.lng}#map=16/${item.purchaseGeo.lat}/${item.purchaseGeo.lng}`}
              target="_blank"
              rel="noreferrer"
            >
              {Number(item.purchaseGeo.lat).toFixed(5)}, {Number(item.purchaseGeo.lng).toFixed(5)}
            </a>
            <span className="small text-muted"> · only you can see this</span>
          </dd>
        </>
      )}
      {piecesLike && (
        <>
          <dt className="col-sm-4 text-muted">Visibility</dt>
          <dd className="col-sm-8">{item.visibility === "PRIVATE" ? "Private — passkey required" : "Public"}</dd>
          {isExternal && (
            <>
              <dt className="col-sm-4 text-muted">On public QR page</dt>
              <dd className="col-sm-8">
                {(item.publicDisplayFields || []).length
                  ? (item.publicDisplayFields as string[])
                    .map((key: string) => PUBLIC_DISPLAY_FIELD_OPTIONS.find((o) => o.key === key)?.label)
                    .filter(Boolean)
                    .join(", ") || "Photo, title, and story only"
                  : "Photo, title, and story only"}
              </dd>
            </>
          )}
        </>
      )}
    </dl>
  );

  return (
    <MyCollectionLayout>
      <McBackLink to={backTo}>{backLabel}</McBackLink>
      <div className="d-flex justify-content-between align-items-start mt-2 flex-wrap gap-2">
        <div>
          <h1 className="h4 mb-1">{item.title}</h1>
          {scannedLike && (
            <p className="small text-muted mb-0">{sawThisLabel(item.lastScannedAt || item.createdAt)}</p>
          )}
          {item.visibility === "PRIVATE" && piecesLike && <Badge color="dark" className="ms-0 mt-1">Private</Badge>}
          {isDemo && <Badge color="secondary" className="ms-0 mt-1">Sample</Badge>}
        </div>
        {piecesLike && !editing && (
          <div className="d-flex flex-wrap gap-2">
            {item.collectorItemId ? (
              <a
                className="mc-btn-primary"
                href={publicPieceUrl(item.collectorItemId)}
                target="_blank"
                rel="noreferrer"
              >
                Preview public page
              </a>
            ) : null}
            <Button className="mc-btn-ghost" onClick={() => { setError(""); setEditing(true); }}>
              Edit story
            </Button>
          </div>
        )}
      </div>

      {editing && piecesLike ? (
        <div className="mt-3">
          <CollectorPieceFields
            values={journal}
            masters={masters}
            mode="edit"
            uploading={uploading}
            coverUploadPercent={coverProgress}
            coverPreview={coverPreview}
            savedCoverUrl={item.coverPhotoUrl || ""}
            fieldErrors={fieldErrors}
            showPublicFields={isExternal}
            onChange={setJournal}
            onUpdate={setJournal}
            onCoverFile={handleCoverFile}
            onMediaError={setError}
            onMediaBusyChange={setMediaBusy}
          />
          {error && (
            <p className="mc-field-error mb-2">
              {error}
            </p>
          )}
          <div className="mc-form-actions">
            <Button className="mc-btn-primary" onClick={saveJournal} disabled={saving || uploading || mediaBusy}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
            <Button
              className="mc-btn-ghost"
              onClick={() => { setJournal(collectorPieceFromItem(item)); dropCoverPreview(); setError(""); setFieldErrors({}); setEditing(false); }}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : scannedLike ? (
        <div className="mt-3">
          {photos}
          {hkpLocked && (
            <div className="mc-card mb-3">
              <p className="mb-2">
                Ask the studio for the full certificate. The workshop can re-share the certificate for this tag.
                The physical HKP tag still verifies authenticity.
              </p>
              {accessRequest?.status === "PENDING" && (
                <p className="small text-muted mb-0">Request sent. Waiting for the workshop.</p>
              )}
              {accessRequest?.status === "DECLINED" && (
                <p className="small text-muted mb-2">The workshop declined this request.</p>
              )}
              {accessRequest?.status !== "PENDING" && (
                <Button className="mc-btn-primary" onClick={submitAccessRequest} disabled={requestingAccess}>
                  {requestingAccess ? "Sending…" : "Ask studio for full certificate"}
                </Button>
              )}
            </div>
          )}
          {isHkp && !item.isSampleItem && item.status === "SCANNED" && (collectorRecordKey(item) || itemId) && (
            <OwnershipClaimCard
              itemId={collectorRecordKey(item) || itemId}
              onOwned={() => window.location.reload()}
            />
          )}
          {preview && (preview.color || preview.category || preview.craftType || preview.cluster) && (
            <dl className="row mb-3">
              <dt className="col-12 text-muted small">What the tag shows</dt>
              {preview.category && <><dt className="col-sm-4 text-muted">Category</dt><dd className="col-sm-8">{preview.category}</dd></>}
              {preview.color && <><dt className="col-sm-4 text-muted">Color</dt><dd className="col-sm-8">{preview.color}</dd></>}
              {preview.craftType && <><dt className="col-sm-4 text-muted">Craft</dt><dd className="col-sm-8">{preview.craftType}</dd></>}
              {preview.cluster && <><dt className="col-sm-4 text-muted">Cluster</dt><dd className="col-sm-8">{preview.cluster}</dd></>}
            </dl>
          )}
          {showCertificate && mongoId && (
            <section className="mb-3">
              <h2 className="h6 text-uppercase text-muted mb-2">Certificate</h2>
              <CertificateTab itemId={mongoId} isSample={item.isSampleItem} />
            </section>
          )}
          {error && <p className="mc-field-error mb-2">{error}</p>}
          <Button className="mc-btn-danger-text mt-3" onClick={archivePiece} disabled={archiving}>
            {archiving ? "Archiving…" : "Archive"}
          </Button>
        </div>
      ) : (
        <div className="mt-3">
          {photos}
          {item.addedVia === "SALE" && (
            <p className="small text-muted mb-2">
              Added from your purchase{item.sourceOrganisationName ? ` at ${item.sourceOrganisationName}` : ""}.
              {item.visibility === "PRIVATE" ? " Private until you choose to share it." : ""}
            </p>
          )}
          {isExternal && !isDemo && !item.isSampleItem && (
            <McAddLaterCard
              // One card per piece: moving to another piece starts its questions fresh.
              key={collectorRecordKey(item) || itemId}
              itemKey={collectorRecordKey(item) || itemId}
              item={item}
              onSaved={(updated) => {
                setItem(updated);
                setJournal(collectorPieceFromItem(updated));
              }}
            />
          )}
          {item.story ? (
            <p className="mc-piece-story" style={{ whiteSpace: "pre-wrap" }}>{item.story}</p>
          ) : (
            <p className="text-muted">No story yet.</p>
          )}
          {privateFacts}
          {showCertificate && mongoId && (
            <section className="mt-4">
              <h2 className="h6 text-uppercase text-muted mb-2">Certificate</h2>
              <CertificateTab itemId={mongoId} isSample={item.isSampleItem} />
            </section>
          )}
          {showSharePrint && (mongoId || item.collectorItemId) && (
            <section className="mc-share-print mt-4" aria-labelledby="mc-share-print-title">
              <h2 id="mc-share-print-title" className="mc-share-print-title">Share &amp; print</h2>
              <p className="small text-muted mt-2">
                Only this piece is shown. Your collection stays private. Price and purchase place never appear.
              </p>
              <CollectorItemQrPanel
                itemId={mongoId || item.collectorItemId}
                collectorItemId={item.collectorItemId}
                title={ownerPieceTitle(item.title)}
                story={item.story}
                coverPhotoUrl={item.coverPhotoUrl}
                format={qrFormat}
                onFormatChange={setQrFormat}
                onPrint={() => { setQrCardOnly(false); setShowQr(true); }}
                onPreviewCard={() => { setQrCardOnly(true); setShowQr(true); }}
              />
            </section>
          )}
          {error && <p className="mc-field-error mb-2">{error}</p>}
          <Button className="mc-btn-danger-text mt-3" onClick={archivePiece} disabled={archiving}>
            {archiving ? "Archiving…" : "Archive"}
          </Button>
        </div>
      )}

      {showSharePrint && (mongoId || item.collectorItemId) && (
        <QrPrintModal
          itemId={mongoId || item.collectorItemId}
          collectorItemId={item.collectorItemId}
          title={ownerPieceTitle(item.title)}
          story={item.story}
          coverPhotoUrl={item.coverPhotoUrl}
          fields={cardFieldSource(item, preview, hkpLocked)}
          isOpen={showQr}
          cardOnly={qrCardOnly}
          initialFormat={qrFormat}
          onFormatChange={setQrFormat}
          onClose={() => setShowQr(false)}
        />
      )}
    </MyCollectionLayout>
  );
};

export default MyCollectionItemDetail;

